import React, { useEffect, useMemo, useState } from 'react';
import axiosConfig from '../../../../../api/axiosConfig';

const TeacherDetailModal = ({ isOpen, onClose, teacher, period, month, year }) => {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState({});
  const [lessonGroups, setLessonGroups] = useState([]);

  const selectedMonth = Number(month) || new Date().getMonth() + 1;
  const selectedYear = Number(year) || new Date().getFullYear();

  useEffect(() => {
    if (isOpen && teacher) {
      fetchTeacherDetails();
      fetchStudentsAndLessons();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, teacher, period, selectedMonth, selectedYear]);

  const getDateRangeForPeriod = () => {
    let startDate;
    let endDate;

    switch (period) {
      case 'daily':
        startDate = new Date();
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'weekly': {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        startDate = new Date(today);
        startDate.setDate(today.getDate() - diff);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
        endDate.setHours(23, 59, 59, 999);
        break;
      }
      case 'monthly':
        startDate = new Date(selectedYear, selectedMonth - 1, 1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(selectedYear, selectedMonth, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'yearly':
        startDate = new Date(selectedYear, 0, 1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(selectedYear, 11, 31);
        endDate.setHours(23, 59, 59, 999);
        break;
      default:
        return { startDate: null, endDate: null };
    }

    return { startDate, endDate };
  };

  const fetchStudentsAndLessons = async () => {
    try {
      const studRes = await axiosConfig.get('/admin/ogrencis');
      const studentsData = studRes.data?.data || studRes.data || [];
      const studentMap = {};

      studentsData.forEach((student) => {
        studentMap[student.id] = student;
      });

      setStudents(studentMap);

      const lessonGroupRes = await axiosConfig.get('/admin/dersgrubu');
      setLessonGroups(lessonGroupRes.data || []);
    } catch (error) {
      console.error('Failed to fetch student/lesson-group data:', error);
    }
  };

  const fetchTeacherDetails = async () => {
    setLoading(true);
    try {
      const response = await axiosConfig.get('/admin/ogretmenders');
      const allLessons = response.data?.data || response.data || [];

      const { startDate, endDate } = getDateRangeForPeriod();

      const teacherLessons = allLessons
        .filter((lesson) => Number(lesson.ogretmen_id) === Number(teacher.id))
        .filter((lesson) => {
          const lessonDateStr = lesson.created_at || lesson.baslangic_tarihi;
          if (!lessonDateStr) return false;

          if (!startDate || !endDate) return true;

          const lessonDate = new Date(lessonDateStr);
          return lessonDate >= startDate && lessonDate <= endDate;
        })
        .sort((a, b) => {
          const dateA = new Date(a.created_at || a.baslangic_tarihi);
          const dateB = new Date(b.created_at || b.baslangic_tarihi);
          return dateB - dateA;
        });

      setLessons(teacherLessons);
    } catch (error) {
      console.error('Failed to fetch teacher lesson details:', error);
      setLessons([]);
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getLessonName = (lessonGroupId) => {
    const lessonGroup = lessonGroups.find((group) => Number(group.id) === Number(lessonGroupId));
    return lessonGroup?.ders_adi || 'Bilinmeyen Ders';
  };

  const getPeriodLabel = () => {
    if (period === 'daily') return 'Bugün';
    if (period === 'weekly') return 'Bu Hafta';

    if (period === 'monthly') {
      return new Date(selectedYear, selectedMonth - 1, 1).toLocaleDateString('tr-TR', {
        month: 'long',
        year: 'numeric'
      });
    }

    if (period === 'yearly') return `${selectedYear}`;
    return 'Dönem';
  };

  const totals = useMemo(() => {
    return lessons.reduce((acc, lesson) => {
      return {
        totalLessons: acc.totalLessons + 1,
        totalAmount: acc.totalAmount + (Number.parseFloat(lesson.ders_ucret) || 0)
      };
    }, { totalLessons: 0, totalAmount: 0 });
  }, [lessons]);

  const durationGroups = useMemo(() => {
    return lessons.reduce((acc, lesson) => {
      const duration = Number(lesson.ders_sure) || 60;
      if (!acc[duration]) {
        acc[duration] = {
          count: 0,
          total: 0
        };
      }

      acc[duration].count += 1;
      acc[duration].total += Number.parseFloat(lesson.ders_ucret) || 0;
      return acc;
    }, {});
  }, [lessons]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-6xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4 pb-4 border-b">
          <div>
            <h3 className="text-2xl font-bold text-gray-800">
              {teacher?.adi} - Ders Detayları
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {getPeriodLabel()} Raporu
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-sm text-blue-600 mb-1">Toplam Ders</div>
                <div className="text-2xl font-bold text-blue-800">{totals.totalLessons}</div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-sm text-green-600 mb-1">Toplam Ücret</div>
                <div className="text-2xl font-bold text-green-800">₺{formatMoney(totals.totalAmount)}</div>
              </div>
              {Object.keys(durationGroups).map((duration) => (
                <div key={duration} className="bg-purple-50 p-4 rounded-lg">
                  <div className="text-sm text-purple-600 mb-1">{duration} dk</div>
                  <div className="text-lg font-bold text-purple-800">{durationGroups[duration].count} ders</div>
                  <div className="text-sm text-purple-600">₺{formatMoney(durationGroups[duration].total)}</div>
                </div>
              ))}
            </div>

            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tarih</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Öğrenci</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ders</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Süre</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ücret</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {lessons.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4 text-gray-500">
                        Bu dönem için ders kaydı bulunamadı
                      </td>
                    </tr>
                  ) : (
                    lessons.map((lesson, index) => {
                      const student = students[lesson.ogrenci_id];
                      const lessonDate = new Date(lesson.created_at || lesson.baslangic_tarihi);

                      return (
                        <tr key={lesson.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-gray-500">{index + 1}</td>
                          <td className="px-4 py-2">
                            <div className="text-sm text-gray-900">{lessonDate.toLocaleDateString('tr-TR')}</div>
                            <div className="text-xs text-gray-500">
                              {lessonDate.toLocaleTimeString('tr-TR', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </td>
                          <td className="px-4 py-2">
                            <div className="text-sm font-medium text-gray-900">{student?.name || 'Bilinmeyen'}</div>
                            {student?.parent_phone && (
                              <div className="text-xs text-gray-500">{student.parent_phone}</div>
                            )}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-600">{getLessonName(lesson.ders_id)}</td>
                          <td className="px-4 py-2 text-center">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {lesson.ders_sure || 60} dk
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right font-medium text-green-600">₺{formatMoney(lesson.ders_ucret)}</td>
                        </tr>
                      );
                    })
                  )}

                  {lessons.length > 0 && (
                    <tr className="bg-gray-50 font-bold">
                      <td colSpan="5" className="px-4 py-3 text-sm text-gray-900">TOPLAM</td>
                      <td className="px-4 py-3 text-right text-green-600">₺{formatMoney(totals.totalAmount)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Kapat
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TeacherDetailModal;
